import {
  observable,
  action,
  computed,
} from 'mobx';
import logger from '../utils/logger';

class JogStore {
  @observable
    jogTable = {};
  @observable
    loading = false;
  @observable
    saving = false;
  @observable
    jogInterval = 0.25;
  @observable
    material = {};
  @observable
    materialId = null;

  constructor(rootStore) {
    this.rootStore = rootStore;
  }

  get api() {
    return this.rootStore.newAuthStore;
  }

  get plantId() {
    return this.rootStore.locationsStore.plantId;
  }

  @computed
  get jogArray() {
    const arr = [];
    for (let i = 1; i <= 50; i += 1) {
      arr.push({
        id: i,
        open: this.jogTable[`open${i}`],
        weight: this.jogTable[`weight${i}`],
      });
    }

    return arr;
  }

  getValue = (id, field) => {
    return this.jogTable[`${field}${id}`];
  };

  @action
    setJogInterval = value => this.jogInterval = value;

  @action
  reset() {
    this.loading = false;
    this.saving = false;
    this.material = null;
    this.materialId = null;
    this.jogTable = {};
  }

  @action
  loadJog(device) {
    const { materialStore } = this.rootStore;
    const m = materialStore.getMaterialForBin(device.id);
    const md =  m ? materialStore.materialDetails[m.materialDetailId] : {};
    this.material = { ...m, ...md };
    this.materialId =  m ? m.itemId : null;

    if (device.jogTable) {
      this.jogTable = { ...device.jogTable };
    } else {
      this.clear();
    }
  }

  @action
  loadMaterial(materialId) {
    const { materialStore } = this.rootStore;
    const m = materialStore.getMaterialByItemId(materialId);
    const md =  m ? materialStore.materialDetails[m.materialDetailId] : {};
    this.material = { ...m, ...md };
    this.materialId =  m ? m.itemId : null;
  }

  @action
    saveJog = (device, jogTable = this.jogTable, onSaved = () => {}) => {
      this.saving = true;

      return this.rootStore.devicesStore.updateDevice(device.id, device.deviceTypeName, { ...device, jogTable }, )
        .then(() => {
          this.reset();
        })
        .finally(() => {
          this.saving = false;
          onSaved();
        });
    };

  @action
    changeJog = (id, fieldName, value) => {
      this.jogTable[`${fieldName}${id}`] = value * 1;
    };

  getWeightForTime(openTime, jogTable) {
    if (!jogTable) {
      return 0;
    }

    let weight = 0;

    Object.keys(jogTable)
      .forEach(key => {
        if (jogTable[key] === openTime) {
          weight = jogTable[key.replace('open', 'weight')];
        }
      });

    return weight;
  }

  getInterpolationDiff(start, end) {
    const countDiff = end - start;
    const weightDiff = this.getValue(end, 'weight') - this.getValue(start, 'weight');
    return Math.ceil(weightDiff / countDiff);
  }

  generateInterpolation(start, end, diff = 1) {
    const startWeight = this.getValue(start, 'weight');

    for (let i = start; i <= end; i += 1) {
      const w = startWeight + diff * (i - start) || 1;
      const weight = w.toFixed(2);
      const currentWeight = this.getValue(i, 'weight');

      if (!currentWeight) {
        this.changeJog(i, 'weight', weight);
      }
    }
  }

  @action
    interpolate = () => {
      let startId = 1;
      let diff = 20;

      for (let i = 0; i <= 50; i += 1) {
        const weight = this.getValue(i, 'weight');
        if (weight > 0) {
          if (i - startId >= 1) {
            diff = this.getInterpolationDiff(startId, i);
            this.generateInterpolation(startId, i, diff);
          }
          startId = i;
        } else if (i === 50) {
          this.generateInterpolation(startId, i, diff);
        }
      }
    };

  @action
    clear = () => {
      this.jogTable = {};
      for (let i = 1; i <= 50; i += 1) {
        this.changeJog(i, 'open', (0.05 + 0.05 * i).toFixed(2));
        this.changeJog(i, 'weight', 0);
      }
    };

  @action
  loadJogHistory(deviceId) {
    this.loading = true;
    return this.api
      .makeAuthorizedRequest(`/api/plant/${this.plantId}/device/${deviceId}/jog/history`);
  };

  @action
  saveJogHistory(deviceId, data) {
    return this.api
      .makeAuthorizedRequest(`/api/plant/${this.plantId}/device/${deviceId}/jog/history`,
        data,
        'POST');
  };

  @action
  saveFeedLogs(batchTicketId, data = []) {
    return this.api
      .makeAuthorizedRequest(`/api/plant/${this.plantId}/batchTicket/${batchTicketId}/loadStatistics`,
        data,
        'POST');
  };

  @action
  updateJogTable(device, jogOpenTime, weight) {
    const { autoJogToleranceBelow, autoJogToleranceAbove } = device;
    const jogTable = device.jogTable;

    if (!jogTable || device.autoTuning) {
      return;
    }

    Object.keys(jogTable).forEach((key) => {
      if (jogOpenTime === jogTable[key] && key.includes('open')) {
        const index = key.split('open')[1];
        const jogTableWeight = jogTable[`weight${index}`];
        const above = jogTableWeight + ((autoJogToleranceAbove * jogTableWeight) / 100);
        const below = jogTableWeight - ((autoJogToleranceBelow * jogTableWeight) / 100);
        logger.log(`check jog table #${device.description}, jog time:`, jogOpenTime, 'jog weight:', jogTableWeight,
          'real weight:', weight,
          'tolerance above:', above,
          'tolerance below:', below);

        if (weight !== jogTableWeight
          && weight <= above
          && weight >= below) {
          jogTable[`weight${index}`] = weight;
          logger.log(`update jog table for #${device.description}:`, jogOpenTime, jogTableWeight, '=>', weight);

          this.saveJog(device, jogTable);
        }
      }
    });
  }
}

export default JogStore;
